import { useCallback, useRef, useState } from "react";
import { recognizeVoice } from "./chatApi";

export const VOICE_STATE = {
  IDLE: "idle",
  RECORDING: "recording",
  PROCESSING: "processing",
  DONE: "done",
  ERROR: "error",
};

const TARGET_RATE = 16000;

const SPEECH_RMS = 0.018;
const SILENCE_HANG_MS = 2000;
const NO_SPEECH_MS = 6000;
const MAX_UTTERANCE_MS = 15000;

const useVoiceOrder = (phraseList, onResult) => {
  const [voiceState, setVoiceState] = useState(VOICE_STATE.IDLE);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);

  const phraseListRef = useRef(phraseList);
  const onResultRef = useRef(onResult);
  phraseListRef.current = phraseList;
  onResultRef.current = onResult;

  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const samplesRef = useRef([]);
  const nativeRateRef = useRef(44100);
  const safetyTimerRef = useRef(null);
  const streamRef = useRef(null);

  const capturingRef = useRef(false);
  const stoppingRef = useRef(false);
  const speechDetectedRef = useRef(false);
  const startTimeRef = useRef(0);
  const lastSpeechRef = useRef(0);
  const noSpeechMsRef = useRef(NO_SPEECH_MS);
  const stopRef = useRef(null);

  const teardown = useCallback(() => {
    capturingRef.current = false;
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    const audioCtx = audioCtxRef.current;
    if (audioCtx) {
      try { sourceRef.current?.disconnect(); } catch { }
      try { processorRef.current?.disconnect(); } catch { }
      try { audioCtx.close(); } catch { }
      audioCtxRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const processAndSend = useCallback(async () => {
    const hadSpeech = speechDetectedRef.current;
    const samples = samplesRef.current;

    if (!hadSpeech || !samples.length) {
      setTranscript("");
      setVoiceState(VOICE_STATE.DONE);
      onResultRef.current("", { hadSpeech: false });
      return;
    }

    const totalSamples = samples.reduce((s, c) => s + c.length, 0);
    const flat = new Float32Array(totalSamples);
    let pos = 0;
    for (const chunk of samples) { flat.set(chunk, pos); pos += chunk.length; }

    const nativeRate = nativeRateRef.current;
    const numFrames = Math.ceil((totalSamples / nativeRate) * TARGET_RATE);

    const offlineCtx = new OfflineAudioContext(1, Math.max(numFrames, 1), TARGET_RATE);
    const buf = offlineCtx.createBuffer(1, totalSamples, nativeRate);
    buf.getChannelData(0).set(flat);

    const src = offlineCtx.createBufferSource();
    src.buffer = buf;
    src.connect(offlineCtx.destination);
    src.start(0);

    const resampled = await offlineCtx.startRendering();
    const channelData = resampled.getChannelData(0);

    const int16 = new Int16Array(channelData.length);
    for (let i = 0; i < channelData.length; i++) {
      int16[i] = Math.max(-32768, Math.min(32767, Math.round(channelData[i] * 32767)));
    }
    const bytes = new Uint8Array(int16.buffer);
    const CHUNK = 8192;
    let binary = "";
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    const pcmBase64 = btoa(binary);

    try {
      const result = await recognizeVoice(pcmBase64, phraseListRef.current);
      const text = result.text || "";
      setTranscript(text);
      setVoiceState(VOICE_STATE.DONE);
      onResultRef.current(text, { hadSpeech: true });
    } catch (err) {
      setError(err?.body?.message || err?.message || "Recognition failed");
      setVoiceState(VOICE_STATE.ERROR);
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (stoppingRef.current || !audioCtxRef.current) return;
    stoppingRef.current = true;
    teardown();
    setVoiceState(VOICE_STATE.PROCESSING);
    await processAndSend();
  }, [teardown, processAndSend]);

  stopRef.current = stopRecording;

  const abort = useCallback(() => {
    stoppingRef.current = true;
    teardown();
    samplesRef.current = [];
    setVoiceState(VOICE_STATE.IDLE);
    setTranscript("");
    setError(null);
  }, [teardown]);

  const startRecording = useCallback(async (opts = {}) => {
    if (capturingRef.current) return;
    setError(null);
    setTranscript("");
    samplesRef.current = [];
    speechDetectedRef.current = false;
    stoppingRef.current = false;
    noSpeechMsRef.current = opts.noSpeechMs ?? NO_SPEECH_MS;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Microphone access denied");
      setVoiceState(VOICE_STATE.ERROR);
      return;
    }
    streamRef.current = stream;

    const audioCtx = new AudioContext();
    nativeRateRef.current = audioCtx.sampleRate;

    const micSource = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);

    startTimeRef.current = performance.now();
    lastSpeechRef.current = startTimeRef.current;
    capturingRef.current = true;

    processor.onaudioprocess = (e) => {
      if (!capturingRef.current) return;
      const data = e.inputBuffer.getChannelData(0);
      samplesRef.current.push(new Float32Array(data));

      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      const now = performance.now();
      if (rms > SPEECH_RMS) {
        speechDetectedRef.current = true;
        lastSpeechRef.current = now;
      }
      const elapsed = now - startTimeRef.current;

      if (speechDetectedRef.current) {
        if (now - lastSpeechRef.current > SILENCE_HANG_MS || elapsed > MAX_UTTERANCE_MS) {
          stopRef.current?.();
        }
      } else if (elapsed > noSpeechMsRef.current) {
        stopRef.current?.();
      }
    };

    micSource.connect(processor);
    const gain = audioCtx.createGain();
    gain.gain.value = 0;
    processor.connect(gain);
    gain.connect(audioCtx.destination);

    audioCtxRef.current = audioCtx;
    processorRef.current = processor;
    sourceRef.current = micSource;

    setVoiceState(VOICE_STATE.RECORDING);

    safetyTimerRef.current = setTimeout(() => {
      stopRef.current?.();
    }, MAX_UTTERANCE_MS + 2000);
  }, []);

  const reset = useCallback(() => {
    setVoiceState(VOICE_STATE.IDLE);
    setTranscript("");
    setError(null);
  }, []);

  return {
    voiceState,
    transcript,
    error,
    startRecording,
    stopRecording,
    abort,
    reset,
  };
};

export default useVoiceOrder;
